/* eslint-disable qwik/no-use-visible-task */
import { $, component$, Slot, useSignal, useVisibleTask$ } from "@builder.io/qwik";
import { useLocation } from "@builder.io/qwik-city";
import { handlePage, useNavigation } from "../../hooks/useNavigation";
import { DealerSearchIcon } from "../../icons/DealerSearchIcon";
import Logo from "../../icons/Logo";
import { PageSearchIcon } from "../../icons/PageSearchIcon";
import type {
    IGenNavigation,
    IGenNavigation_ExampleSearches,
    IGenNavigationCategory,
    IGenPage,
} from "../../services/graphql/__generated/sdk";
import { DEFAULT_LOCALE } from "../../services/i18n";
import { cn } from "../../utils/tailwind-helper";
import { NavigationMenuLeft } from "./navigation-menu-left/NavigationMenuLeft";
import { NavigationSearchRight } from "./navigation-search-right/NavigationSearchRight";
import { NavigationHighlight } from "./NavigationHighlight";
import { useAnimatedBorder } from "~/hooks/useAnimatedBorder";
import type { Maybe } from "graphql/jsutils/Maybe";
import { scheduleAnimationCallback } from "~/utils/scheduleAnimationCallback";
import { HERO_COMPONENTS } from "../../layouts/hero-components";
import { Subnavigation, SubnavigationItem } from "./Subnavigation";
import { useUniversalText } from "~/hooks/useCMSData";
import { CatalogIcon } from "~/icons/CatalogIcon";
import { NavigationMenuButton } from "./NavigationMenuButton";

export interface NavigationProps extends IGenNavigation {
    firstComponentTypename?: string;
    subnavigationTitle?: string | null | undefined;
    subnavigationItems?: IGenPage["subnavigationItems"] | null | undefined;
    partnerDeployment?: boolean;
}

const thresholdPixel = 100;

export const Navigation = component$<NavigationProps>(
    ({
        items,
        firstComponentTypename,
        dealerSearchPage,
        exampleSearches,
        catalogPage,
        homePage,
        subnavigationItems,
        subnavigationTitle,
    }) => {
        const menuLeftOpen = useSignal(false);
        const searchRightOpen = useSignal(false);
        const highlightMiddleOpen = useSignal(false);
        const activeHighlightIndex = useSignal(-1);
        const scrolledPastTopThreshold = useSignal(false);
        const { currentLocale } = useNavigation();
        const location = useLocation();
        const borderRefs: { [key: string]: HTMLElement } = {};
        const hoveringItemId = useSignal<string | null>(null);
        const lastActiveItemIndex = useSignal<string>(null!);
        const willAnimate = useSignal(false);
        const MENU_LEFT_DURATION = 500;
        const mobileSubmenuOpen = useSignal(false);
        const { findASpecializedRetailerNearYou = "Find your dealer" } = useUniversalText();

        const scrollControl = {
            disableScroll: $((hideBorder?: boolean) => {
                document.body.classList.add("overflow-hidden", "mr-[15px]");
                if (!hideBorder) {
                    document.body.classList.add("border-r", "border-r-[#e5e7eb]");
                }
            }),
            enableScroll: $(() => {
                document.body.classList.remove("overflow-hidden", "mr-[15px]", "border-r", "border-r-[#e5e7eb]");
            }),
        };

        const {
            ANIMATION_DURATION: HIGHLIGHT_ANIMATION_DURATION,
            mouseEnterHandler$,
            mouseLeaveHandler$,
        } = useAnimatedBorder({
            borderRefs,
            hoveringItemId,
        });

        const toggleMenuLeft$ = $(() => {
            if (menuLeftOpen.value) {
                willAnimate.value = true;

                scheduleAnimationCallback(() => {
                    scrollControl.enableScroll();
                }, MENU_LEFT_DURATION);

                scheduleAnimationCallback(() => {
                    menuLeftOpen.value = false;
                    willAnimate.value = false;
                }, MENU_LEFT_DURATION);
            } else {
                menuLeftOpen.value = true;
                scrollControl.disableScroll();
            }
        });

        const togglePageSearchRight$ = $(() => {
            if (searchRightOpen.value) {
                willAnimate.value = true;

                scheduleAnimationCallback(() => {
                    scrollControl.enableScroll();
                }, MENU_LEFT_DURATION);

                scheduleAnimationCallback(
                    () => {
                        searchRightOpen.value = false;
                        willAnimate.value = false;
                    },
                    (MENU_LEFT_DURATION + 50) * 1.5,
                );
            } else {
                searchRightOpen.value = true;
                scrollControl.disableScroll();
            }
        });

        const toggleHighlightMiddle$ = $((itemIndex: number) => {
            if (highlightMiddleOpen.value) {
                scheduleAnimationCallback(() => {
                    scrollControl.enableScroll();
                }, HIGHLIGHT_ANIMATION_DURATION * 0.75);
            } else {
                scrollControl.disableScroll(true);
            }
            if (activeHighlightIndex.value === itemIndex) {
                highlightMiddleOpen.value = false;
                scheduleAnimationCallback(() => {
                    activeHighlightIndex.value = -1;
                }, HIGHLIGHT_ANIMATION_DURATION * 0.85);
            } else if (itemIndex !== -1) {
                highlightMiddleOpen.value = true;
                willAnimate.value = true;
                scheduleAnimationCallback(() => {
                    activeHighlightIndex.value = itemIndex;
                    willAnimate.value = false;
                }, HIGHLIGHT_ANIMATION_DURATION / 2);
            } else if (itemIndex === -1) {
                activeHighlightIndex.value = itemIndex;
                highlightMiddleOpen.value = true;
            }
        });

        const directionThreshold = 50;
        const scrollDirection = useSignal<"up" | "down" | null>(null);

        useVisibleTask$(() => {
            let lastScrollPosition = window.scrollY;

            const handleScroll = () => {
                const currentScrollPosition = window.scrollY;

                // Track scroll direction with 50px threshold
                if (Math.abs(currentScrollPosition - lastScrollPosition) >= directionThreshold) {
                    scrollDirection.value = currentScrollPosition > lastScrollPosition ? "down" : "up";
                    lastScrollPosition = currentScrollPosition;
                }

                // Original threshold tracking
                if (currentScrollPosition >= thresholdPixel) {
                    // scrolled past threshold
                    if (!scrolledPastTopThreshold.value) {
                        scrolledPastTopThreshold.value = true;
                    }
                } else {
                    // scrolled top
                    if (scrolledPastTopThreshold.value) {
                        scrolledPastTopThreshold.value = false;
                    }
                }
            };

            document.addEventListener("scroll", handleScroll);
            handleScroll();

            return () => {
                document.removeEventListener("scroll", handleScroll);
            };
        });

        const homeLink = currentLocale == DEFAULT_LOCALE ? "/" : `/${currentLocale}`;
        const homeLinkTitle = currentLocale == "en" ? "Back to home" : "Zurück zur Startseite";

        const dealerSearchPageWithFallback = (dealerSearchPage || {
            name: "Dealer Search",
            slug: "dealer-search",
        }) as IGenPage;

        const catalogPageWithFallback = (catalogPage || {
            name: "Catalog",
            slug: "catalog",
        }) as IGenPage;

        const dealerSearchLink = handlePage({
            page: dealerSearchPageWithFallback,
            currentLocale,
            homePage,
            currentUrl: location.url,
        });

        const catalogLink = handlePage({
            page: catalogPageWithFallback,
            currentLocale,
            homePage,
            currentUrl: location.url,
        });

        const transparentModeOnLoad = HERO_COMPONENTS.includes(`${firstComponentTypename}`);
        const transparentModeOnScroll = !scrolledPastTopThreshold.value;
        const transparentMode = transparentModeOnLoad && transparentModeOnScroll && !highlightMiddleOpen.value;
        const hasSubnavigation = subnavigationTitle || (subnavigationItems && subnavigationItems.length > 0);

        const mainTextColor = cn(
            "transition-colors duration-300",
            highlightMiddleOpen.value || mobileSubmenuOpen.value
                ? "text-primary-500"
                : transparentMode
                  ? cn("text-white", activeHighlightIndex.value !== -1 && "delay-200")
                  : "text-primary-500",
        );

        const highlightCategories = items?.filter((i) => i && i?.__typename === "NavigationCategory")?.slice(0, 3);

        const iconHoverStyles = cn(
            transparentMode && !mobileSubmenuOpen.value
                ? "border-transparent bg-white/10 backdrop-blur-md hover:bg-white/30 [&_svg]:text-white"
                : "border-primary-500/5 bg-transparent hover:border-primary-500/10 hover:bg-primary-500/10",
        );

        const iconContainerStyles = cn(
            "flex h-10 w-10 shrink-0 items-center justify-center rounded-full border transition-colors",
        );

        return (
            <div id="mainnav" class={menuLeftOpen.value ? "open" : ""}>
                <header
                    class={cn(
                        "sticky top-0 z-40",
                        (!hasSubnavigation || highlightMiddleOpen.value) && "border-b",
                        transparentMode ? "border-b-white/5" : "border-b-primary-500/5",
                        "transition-transform duration-300",
                        !transparentMode && hasSubnavigation && scrollDirection.value == "down"
                            ? "translate-y-[-90px]"
                            : "translate-y-0",
                    )}
                >
                    <div
                        class={cn(
                            "relative duration-300 after:absolute after:left-0 after:top-0 after:h-full after:w-full after:transition-transform after:duration-300",
                            transparentMode && !mobileSubmenuOpen.value
                                ? "after:-translate-y-full"
                                : "after:translate-y-0",
                            highlightMiddleOpen.value
                                ? "after:bg-white"
                                : "after:bg-white/90 after:backdrop-blur-[15px]",
                        )}
                    >
                        <div class="relative z-40 mx-auto">
                            <div
                                class={cn(
                                    "grid grid-cols-4 items-center justify-between gap-x-[16px] px-[16px] py-[24px]",
                                    "silver:grid-cols-8 silver:gap-x-[24px] silver:px-[32px]",
                                    "gold:grid-cols-12 gold:gap-x-[24px] gold:px-[40px]",
                                    "platinum:mx-auto platinum:max-w-[1440px] platinum:grid-cols-12 platinum:gap-x-[24px] platinum:px-[40px]",
                                )}
                            >
                                {/* Logo - Always in the first column(s) */}
                                <div
                                    class={cn(
                                        "z-20 col-span-1 flex items-center justify-start", // Adjusted col-span
                                        "gold:col-span-2", // Adjusted col-span
                                        "platinum:col-span-3", // Adjusted col-span
                                    )}
                                >
                                    <a
                                        href={homeLink}
                                        title={homeLinkTitle}
                                        class={cn(
                                            "flex h-[28px] items-center",
                                            "gold:h-[32px] gold:w-[144.8px]",
                                            "platinum:h-[32px] platinum:w-[144.8px]",
                                            mainTextColor,
                                        )}
                                    >
                                        <Logo class={cn("h-full")} />
                                    </a>
                                </div>

                                {/* Navigation Links - Now occupying the center columns */}
                                <div
                                    class={cn(
                                        "hidden",
                                        "gold:col-span-6 gold:col-start-4 gold:flex gold:items-center gold:justify-center gold:gap-[48px]", // Explicit column assignment
                                        "platinum:col-span-6 platinum:col-start-4 platinum:flex platinum:items-center platinum:justify-center platinum:gap-[48px]", // Explicit column assignment
                                    )}
                                >
                                    {highlightCategories?.map((item, index) => {
                                        if (!item || !item.id) return null;
                                        return (
                                            <div
                                                onMouseEnter$={() => mouseEnterHandler$(item.id!)}
                                                onMouseLeave$={() => mouseLeaveHandler$(item.id!)}
                                                onClick$={() => {
                                                    lastActiveItemIndex.value = activeHighlightIndex.value.toString();
                                                    toggleHighlightMiddle$(index);
                                                }}
                                                key={"navh" + item!.id + index}
                                                role="button"
                                                class={cn(
                                                    "group relative cursor-pointer pb-1 text-[16px] font-normal leading-[24px]",
                                                    mainTextColor,
                                                )}
                                            >
                                                <span>{item?.name || ""}</span>
                                                <div
                                                    ref={(el) =>
                                                        el &&
                                                        activeHighlightIndex.value !== index &&
                                                        (borderRefs[item.id!] = el)
                                                    }
                                                    class={cn(
                                                        "absolute bottom-0 left-0 h-px w-full scale-x-0",
                                                        transparentMode && !highlightMiddleOpen.value
                                                            ? "bg-white/80"
                                                            : "bg-primary-500/80",
                                                        activeHighlightIndex.value === index
                                                            ? "animate-[borderIn_forwards] group-hover:animate-[borderOut_forwards]"
                                                            : lastActiveItemIndex.value === index.toString() &&
                                                                  "animate-[borderOut_forwards]",
                                                    )}
                                                    style={
                                                        activeHighlightIndex.value !== index
                                                            ? {
                                                                  animationDuration: `${HIGHLIGHT_ANIMATION_DURATION}ms`,
                                                                  animationFillMode: "forwards",
                                                                  animationTimingFunction: "ease",
                                                              }
                                                            : {
                                                                  animationDuration: `${HIGHLIGHT_ANIMATION_DURATION}ms`,
                                                              }
                                                    }
                                                />
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* Right side actions - Always in the last column(s) */}
                                <div
                                    class={cn(
                                        "z-20 col-span-3 col-start-4 flex items-center justify-end gap-x-[12px]", // Adjusted col-span and start
                                        "silver:col-span-4 silver:col-start-6", // Adjusted col-span and start
                                        "gold:col-span-3 gold:col-start-10 gold:gap-x-[12px]", // Adjusted col-span and start
                                        "platinum:col-span-4 platinum:col-start-10 platinum:gap-x-[12px]", // Adjusted col-span and start
                                    )}
                                >
                                    <a class={cn("hidden shrink-0 silver:block")} {...dealerSearchLink}>
                                        <button
                                            class={cn(
                                                "flex h-10 items-center justify-center rounded-full border px-4 py-2 transition-colors",
                                                iconHoverStyles,
                                                "text-[14px] font-normal leading-[normal]",
                                                mainTextColor,
                                            )}
                                        >
                                            {findASpecializedRetailerNearYou}
                                        </button>
                                    </a>

                                    <a class={cn("hidden silver:block")} {...catalogLink}>
                                        <button class={cn(iconContainerStyles, iconHoverStyles, mainTextColor)}>
                                            <CatalogIcon />
                                        </button>
                                    </a>

                                    <a {...dealerSearchLink} class="silver:hidden">
                                        <button class={cn(iconContainerStyles, iconHoverStyles, mainTextColor)}>
                                            <DealerSearchIcon />
                                        </button>
                                    </a>
                                    <button
                                        onClick$={togglePageSearchRight$}
                                        class={cn(iconContainerStyles, iconHoverStyles, mainTextColor)}
                                    >
                                        <PageSearchIcon />
                                    </button>
                                    <NavigationMenuButton
                                        highlightMiddleOpen={highlightMiddleOpen.value}
                                        mobileSubmenuOpen={mobileSubmenuOpen.value}
                                        toggleMenuLeft$={toggleMenuLeft$}
                                        transparentMode={transparentMode}
                                        activeHighlightIndex={activeHighlightIndex.value}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                    {hasSubnavigation && (
                        <>
                            <div
                                class={cn(
                                    "subnavigationcontainer absolute z-40 w-full border-t transition-[background-color,backdrop-filter,opacity] duration-300",
                                    !mobileSubmenuOpen.value && "border-b",
                                    highlightMiddleOpen.value && "pointer-events-none opacity-0 delay-100",
                                    transparentMode && !mobileSubmenuOpen.value
                                        ? "bg-transparent"
                                        : "bg-white/90 backdrop-blur-[15px]",
                                    !transparentMode
                                        ? "border-b-primary-500/5 border-t-primary-500/5"
                                        : "border-b-transparent border-t-white/5",
                                )}
                            >
                                <Subnavigation
                                    mainTextColor={mainTextColor}
                                    subnavigationItems={subnavigationItems}
                                    subnavigationTitle={subnavigationTitle}
                                    mobileSubmenuOpen={mobileSubmenuOpen}
                                />
                            </div>
                            {/* bronze only */}
                            <div
                                class={cn(
                                    "absolute z-40 col-span-4 mt-[73px] w-full",
                                    "[background-color,backdrop-filter] duration-300",
                                    "silver:hidden",
                                    !mobileSubmenuOpen.value && transparentMode ? "bg-transparent" : "bg-white/90",
                                    mobileSubmenuOpen.value ? "delay-0" : "delay-[600ms]",
                                )}
                            >
                                <div
                                    class={cn(
                                        "flex-col justify-end px-[16px] pb-[24px]",
                                        "translate-x-0 translate-y-0 transition-[opacity,transform] duration-300",
                                        mobileSubmenuOpen.value
                                            ? "translate-x-0 translate-y-0 border-b border-b-primary-500/5 opacity-100 delay-0"
                                            : "pointer-events-none h-0 -translate-x-1 p-0 opacity-0 delay-0 duration-0",
                                    )}
                                >
                                    {subnavigationItems?.map((item, index) => {
                                        if (!item) return null;
                                        return (
                                            <SubnavigationItem
                                                index={index}
                                                item={item}
                                                mainTextColor={mainTextColor}
                                                key={`sn${item?.id}${index}`}
                                            />
                                        );
                                    })}
                                </div>
                            </div>
                        </>
                    )}
                </header>
                <Slot />
                <section class={cn(!highlightMiddleOpen.value && "pointer-events-none")}>
                    <div
                        class={cn(
                            "fixed bottom-0 left-0 right-0 top-[88px] z-[39] h-fit w-full translate-y-[calc(-100%-88px)] transition-transform",
                            highlightMiddleOpen.value && "translate-y-[0%]",
                        )}
                        style={{ transitionDuration: `${HIGHLIGHT_ANIMATION_DURATION}ms` }}
                    >
                        <div class={cn("overflow-hidden")}>
                            <NavigationHighlight
                                rightSide={activeHighlightIndex.value === 2 ? "tripple" : "single"}
                                willAnimate={willAnimate.value}
                                highlightCategories={highlightCategories}
                                categoy={
                                    highlightCategories?.[activeHighlightIndex.value] as
                                        | Maybe<IGenNavigationCategory>
                                        | undefined
                                }
                            />
                        </div>
                    </div>
                    <div
                        class={cn(
                            "fixed bottom-0 left-0 right-0 top-[90px] z-30",
                            "transition-[background-color,backdrop-filter]",
                            highlightMiddleOpen.value && "bg-primary-500/30 backdrop-blur-[30px]",
                        )}
                        style={{
                            transitionDuration: `${HIGHLIGHT_ANIMATION_DURATION * 0.75}ms`,
                            transitionDelay: highlightMiddleOpen.value
                                ? `${HIGHLIGHT_ANIMATION_DURATION * 0.25}ms`
                                : "0ms",
                        }}
                        onClick$={() => toggleHighlightMiddle$(activeHighlightIndex.value)}
                    />
                </section>
                <section
                    class={cn("fixed inset-0 z-50 h-full w-full", searchRightOpen.value ? "block" : "hidden")}
                    onClick$={togglePageSearchRight$}
                >
                    <div class="pointer-events-none h-full" onClick$={(e) => e.stopPropagation()}>
                        <NavigationSearchRight
                            togglePageSearchRight$={togglePageSearchRight$}
                            exampleSearches={(exampleSearches || []) as IGenNavigation_ExampleSearches[]}
                            transitionDuration={MENU_LEFT_DURATION}
                            willToggle={willAnimate.value}
                        />
                    </div>
                </section>
                <nav
                    class={cn("fixed inset-0 z-50 h-full w-full", menuLeftOpen.value ? "block" : "hidden")}
                    onClick$={toggleMenuLeft$}
                >
                    <div class="pointer-events-none h-full" onClick$={(e) => e.stopPropagation()}>
                        <NavigationMenuLeft
                            toggleMenuLeft$={toggleMenuLeft$}
                            items={items || []}
                            dealerSearchPageWithFallback={dealerSearchPageWithFallback}
                            transitionDuration={MENU_LEFT_DURATION}
                            willClose={willAnimate.value}
                        />
                    </div>
                </nav>
            </div>
        );
    },
);
